/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { PageHeader, PhotoTitleCard, type SplitFeatureSectionPicture } from '@repo/shared-client/components/blocks'
import { createFileRoute, Link } from '@tanstack/react-router'
import adultProductsPicture from '@web/src/assets/vendor-market/vm-adult-products.webp?w=400;800;1200&format=avif;webp&as=picture'
import allVendorsPicture from '@web/src/assets/vendor-market/vm-all.webp?w=400;800;1200&format=avif;webp&as=picture'
import apparelPicture from '@web/src/assets/vendor-market/vm-apparel.webp?w=400;800;1200&format=avif;webp&as=picture'
import automotivePicture from '@web/src/assets/vendor-market/vm-automotive.webp?w=400;800;1200&format=avif;webp&as=picture'
import beautyPicture from '@web/src/assets/vendor-market/vm-beauty.webp?w=400;800;1200&format=avif;webp&as=picture'
import entertainmentPicture from '@web/src/assets/vendor-market/vm-entertainment.webp?w=400;800;1200&format=avif;webp&as=picture'
import eventsPicture from '@web/src/assets/vendor-market/vm-events.webp?w=400;800;1200&format=avif;webp&as=picture'
import furniturePicture from '@web/src/assets/vendor-market/vm-furniture.webp?w=400;800;1200&format=avif;webp&as=picture'
import generalLaborPicture from '@web/src/assets/vendor-market/vm-general-labor.webp?w=400;800;1200&format=avif;webp&as=picture'
import healthPicture from '@web/src/assets/vendor-market/vm-health.webp?w=400;800;1200&format=avif;webp&as=picture'
import mediaPicture from '@web/src/assets/vendor-market/vm-media.webp?w=400;800;1200&format=avif;webp&as=picture'
import otherPicture from '@web/src/assets/vendor-market/vm-other.webp?w=400;800;1200&format=avif;webp&as=picture'
import professionalServicesPicture from '@web/src/assets/vendor-market/vm-professional-services.webp?w=400;800;1200&format=avif;webp&as=picture'
import tradesPicture from '@web/src/assets/vendor-market/vm-trades.webp?w=400;800;1200&format=avif;webp&as=picture'
import wellnessPicture from '@web/src/assets/vendor-market/vm-wellness.webp?w=400;800;1200&format=avif;webp&as=picture'
import VmMenu from '@web/src/features/vendor-market/vm-menu/vm-menu'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { useTranslation } from '@web/src/lib/intl/react'
import type { VendorMarketCategoryEnum } from 'types/graphql'
import { vendorMarketCategoriesIndexHeadFromLoaderData } from './index.seo'

type VMCategoryCountsQuery = {
  vendorMarketCategoryCounts: {
    allVendors: number
    adultProducts: number
    apparel: number
    automotive: number
    beauty: number
    entertainment: number
    events: number
    furniture: number
    generalLabor: number
    health: number
    media: number
    other: number
    professionalServices: number
    trades: number
    wellness: number
  }
}

const VM_CATEGORY_COUNTS_QUERY: TypedDocumentNode<VMCategoryCountsQuery> = gql`
  query VMCategoryCountsQuery {
    vendorMarketCategoryCounts {
      allVendors
      adultProducts
      apparel
      automotive
      beauty
      entertainment
      events
      furniture
      generalLabor
      health
      media
      other
      professionalServices
      trades
      wellness
    }
  }
`

export const Route = createFileRoute('/(public)/(main-layout)/vendor-market/')({
  component: VMCategoryTilesPage,
  head: ({ loaderData }) => vendorMarketCategoriesIndexHeadFromLoaderData(loaderData),
  loader: async ({ context }) => {
    const { queryData, queryRef } = await queryAndPreload(context, VM_CATEGORY_COUNTS_QUERY)
    return {
      vendorMarketCategoryCountsQueryRef: queryRef,
      vendorMarketCategoryCountsQueryData: queryData,
      vendorMarketCategoryCounts: queryData?.vendorMarketCategoryCounts ?? null,
    }
  },
})

type CategoryCardConfig = {
  id: string
  titleKey: string
  imagePicture: SplitFeatureSectionPicture
  imageY?: string | number
  count: number
  category?: VendorMarketCategoryEnum
  desktopSpan?: { colSpan?: number; rowSpan?: number }
}

function VMCategoryTilesPage() {
  const { t } = useTranslation()
  const loaderData = Route.useLoaderData()
  const { isAuthenticated } = useAuth()
  const { data } = useReadQuery(loaderData.vendorMarketCategoryCountsQueryRef as QueryRef<VMCategoryCountsQuery>)
  const counts =
    (data ?? loaderData.vendorMarketCategoryCountsQueryData)?.vendorMarketCategoryCounts ??
    loaderData.vendorMarketCategoryCounts

  /** Keep tile ids, categories aligned with `CATEGORY_TILES_FOR_JSON_LD` in `./index.seo.ts`. */
  const cards: CategoryCardConfig[] = [
    {
      id: 'all-vendors',
      titleKey: t('All Vendors'),
      imagePicture: allVendorsPicture,
      count: counts?.allVendors ?? 0,
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'apparel',
      titleKey: t('Apparel'),
      imagePicture: apparelPicture,
      count: counts?.apparel ?? 0,
      category: 'APPAREL',
      imageY: '60%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
    {
      id: 'adult-products',
      titleKey: t('Adult products'),
      imagePicture: adultProductsPicture,
      count: counts?.adultProducts ?? 0,
      category: 'ADULT_PRODUCTS',
      imageY: '55%',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'automotive',
      titleKey: t('Automotive'),
      imagePicture: automotivePicture,
      count: counts?.automotive ?? 0,
      category: 'AUTOMOTIVE',
      imageY: '55%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
    {
      id: 'beauty',
      titleKey: t('Beauty'),
      imagePicture: beautyPicture,
      count: counts?.beauty ?? 0,
      category: 'BEAUTY',
      imageY: '40%',
      desktopSpan: { colSpan: 2, rowSpan: 1 },
    },
    {
      id: 'entertainment',
      titleKey: t('Entertainment'),
      imagePicture: entertainmentPicture,
      count: counts?.entertainment ?? 0,
      category: 'ENTERTAINMENT',
      imageY: '60%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
    {
      id: 'events',
      titleKey: t('Events'),
      imagePicture: eventsPicture,
      count: counts?.events ?? 0,
      category: 'EVENTS',
      imageY: '50%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
    {
      id: 'furniture',
      titleKey: t('Furniture'),
      imagePicture: furniturePicture,
      count: counts?.furniture ?? 0,
      category: 'FURNITURE',
      imageY: '50%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
    {
      id: 'general-labor',
      titleKey: t('General labor'),
      imagePicture: generalLaborPicture,
      count: counts?.generalLabor ?? 0,
      category: 'GENERAL_LABOR',
      imageY: '50%',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'health',
      titleKey: t('Health'),
      imagePicture: healthPicture,
      count: counts?.health ?? 0,
      category: 'HEALTH',
      imageY: '50%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
    {
      id: 'media',
      titleKey: t('Media'),
      imagePicture: mediaPicture,
      count: counts?.media ?? 0,
      category: 'MEDIA',
      imageY: '50%',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'trades',
      titleKey: t('Trades'),
      imagePicture: tradesPicture,
      count: counts?.trades ?? 0,
      category: 'TRADES',
      imageY: '60%',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'professional-services',
      titleKey: t('Professional services'),
      imagePicture: professionalServicesPicture,
      count: counts?.professionalServices ?? 0,
      category: 'PROFESSIONAL_SERVICES',
      imageY: '55%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },

    {
      id: 'wellness',
      titleKey: t('Wellness'),
      imagePicture: wellnessPicture,
      count: counts?.wellness ?? 0,
      category: 'WELLNESS',
      imageY: '50%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
    {
      id: 'other',
      titleKey: t('Other'),
      imagePicture: otherPicture,
      count: counts?.other ?? 0,
      category: 'OTHER',
      imageY: '50%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
  ]

  return (
    <div className="mx-auto w-full max-w-7xl px-4 pt-2 pb-10 md:px-16 md:pt-4 md:pb-16">
      <PageHeader
        breadcrumbItems={[{ label: t('Home'), to: '/' }, { label: t('Vendor Market') }]}
        highlightTitle={t('Market')}
        preTitle={t('Vendor')}
        subtitle={t('Browse vendor categories to quickly find professionals that match your needs.')}
      />

      <div className="my-4">
        <VmMenu activeTab="categories" />
      </div>

      <div className="grid w-full auto-rows-auto grid-cols-1 gap-3 pt-4 md:auto-rows-[180px] md:grid-cols-3 md:gap-6 md:pt-0">
        {cards.map((card) => (
          <div
            className={[
              'col-span-1 row-span-1 h-full',
              (card.desktopSpan?.colSpan ?? 1) === 2 ? 'md:col-span-2' : 'md:col-span-1',
              (card.desktopSpan?.rowSpan ?? 1) === 2 ? 'md:row-span-2' : 'md:row-span-1',
            ].join(' ')}
            key={card.id}
          >
            <PhotoTitleCard
              imageAlt={t('Background image for {{title}}', { title: card.titleKey })}
              imagePicture={card.imagePicture}
              imagePositionY={card.imageY}
              rootProps={{
                as: Link,
                className: 'block w-full',
                ...(isAuthenticated
                  ? {
                      to: '/vendor-market/list',
                      search: { page: 1, ...(card.category ? { categories: [card.category] } : {}) },
                    }
                  : { to: '/auth/signup' }),
              }}
              subtitle={t('{{count}} Vendors', { count: card.count })}
              title={card.titleKey}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
